/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren, Fragment } from 'preact';
import { classNames } from '../utils/UNSAFE_classNames';
import { messageDetailStyles } from './themes/MessageStyles.css';
import { MessageRendererDataItem, MessageVariant } from './Message.types';
import { isValidValueForProp } from './MessageFormattingUtils';

type MessageWithDetailText = {
  /**
   * The detail text for the message
   */
  detail?: string;
};

/**
 * Props for the Detail component
 */
type Props<K, D> = {
  /**
   * The data item for rendering the detail content
   */
  item: MessageRendererDataItem<K, D>;

  /**
   * A custom renderer for rendering the detail content
   */
  renderer?: (item: MessageRendererDataItem<K, D>) => ComponentChildren;

  /**
   * Style variant
   */
  variant?: MessageVariant;
};

/**
 * Default renderer for rendering the detail content.
 *
 * @param item The data item object
 * @returns Rendered detail content
 */
function defaultDetailRenderer<D extends MessageWithDetailText>(
  item: MessageRendererDataItem<unknown, D>
): ComponentChildren {
  const { detail } = item.data;
  // If the detail is null or an empty string, do not render the
  // content row
  if (!isValidValueForProp(detail)) {
    return null;
  }
  return <Fragment>{detail}</Fragment>;
}

/**
 * Detail Component for rendering the detail content of the Message
 */
function MessageDetail<K, D extends MessageWithDetailText>({
  item,
  renderer,
  variant = 'banner'
}: Props<K, D>) {
  const isCustomRendered = renderer != null;
  const renderedContent = (renderer ?? defaultDetailRenderer)(item);

  if (renderedContent == null) return null;

  // If detail content is rendered, then wrap it in a div with specified style classes
  // TODO: JET-48715 - remove marker classes
  // Right now, oj-message-banner component uses this for unit tests and has no alternate way for
  // testing. This will need to be removed as we work on oj-c-message-banner.
  const markerClassForTests = 'oj-c-messagebanner-detail';
  const classes = classNames([
    variant === 'banner' && markerClassForTests,
    messageDetailStyles.base,
    messageDetailStyles[variant]
  ]);
  return (
    <div class={classes} {...(isCustomRendered ? { 'data-oj-message-custom-detail': '' } : {})}>
      {renderedContent}
    </div>
  );
}

export { MessageDetail };
